import React, { useState } from 'react';
import { Heart, Scissors, ArrowLeft, ShoppingCart, Globe, Minus, Plus, CreditCard, Stethoscope, BookOpen, GraduationCap, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSubscription } from '../hooks/useSubscription';
import { supabase } from '../lib/supabase';
import { stripeProducts } from '../stripe-config';

interface CartItem {
  id: string;
  quantity: number;
}

interface Prompt {
  id: string;
  en: {
    title: string;
    description: string;
    category: string;
    features: string[];
  };
  ar: {
    title: string;
    description: string;
    category: string;
    features: string[];
  };
  price: number;
  icon: React.ReactNode;
}

const MedicalPrompts = () => {
  const [currentLanguage, setCurrentLanguage] = useState<'en' | 'ar'>('en');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [showCart, setShowCart] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  const { user, isAuthenticated } = useAuth();
  const { activeSubscriptionPlan } = useSubscription();

  const prompts: Prompt[] = [
    {
      id: 'medical-report-prompt',
      en: {
        title: 'Medical Report Prompt [ internal medicine form ]',
        description: 'The prompt writes patient profile - chief complaint - hopi - systematic review - pmx + psx - medication history - allergies - family history - social history',
        category: 'Medical Reports',
        features: [
          'Patient History Templates',
          'Physical Examination Guides',
          'Differential Diagnosis Framework',
          'Treatment Plan Templates',
          'Follow-up Protocols'
        ]
      },
      ar: {
        title: 'قالب التقرير الطبي [ نموذج الطب الباطني ]',
        description: 'يكتب القالب ملف المريض - الشكوى الرئيسية - تاريخ المرض الحالي - المراجعة المنهجية - التاريخ المرضي والجراحي - تاريخ الأدوية - الحساسية - التاريخ العائلي - التاريخ الاجتماعي',
        category: 'التقارير الطبية',
        features: [
          'قوالب التاريخ المرضي',
          'أدلة الفحص السريري',
          'إطار التشخيص التفريقي',
          'قوالب خطة العلاج',
          'بروتوكولات المتابعة'
        ]
      },
      price: 4.99,
      icon: <Heart className="w-8 h-8" />
    },
    {
      id: 'medical-report-prompt-surgery',
      en: {
        title: 'Medical Report Prompt [ Surgery Form ]',
        description: 'Specialized surgical report template for comprehensive pre-operative, intra-operative, and post-operative documentation',
        category: 'Surgical Reports',
        features: [
          'Pre-operative Assessment Templates',
          'Surgical Procedure Documentation',
          'Post-operative Care Plans',
          'Complication Management Guides',
          'Recovery Protocols'
        ]
      },
      ar: {
        title: 'قالب التقرير الطبي [ نموذج الجراحة ]',
        description: 'قالب تقرير جراحي متخصص للتوثيق الشامل قبل وأثناء وبعد العمليات الجراحية',
        category: 'التقارير الجراحية',
        features: [
          'قوالب التقييم قبل الجراحة',
          'توثيق الإجراءات الجراحية',
          'خطط الرعاية بعد الجراحة',
          'أدلة إدارة المضاعفات',
          'بروتوكولات التعافي'
        ]
      },
      price: 4.99,
      icon: <Scissors className="w-8 h-8" />
    }
  ];

  const toggleLanguage = () => {
    setCurrentLanguage(currentLanguage === 'en' ? 'ar' : 'en');
  };

  const addToCart = (promptId: string) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === promptId);
      if (existingItem) {
        return prevCart.map(item =>
          item.id === promptId
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        return [...prevCart, { id: promptId, quantity: 1 }];
      }
    });
  };

  const removeFromCart = (promptId: string) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === promptId);
      if (existingItem && existingItem.quantity > 1) {
        return prevCart.map(item =>
          item.id === promptId
            ? { ...item, quantity: item.quantity - 1 }
            : item
        );
      } else {
        return prevCart.filter(item => item.id !== promptId);
      }
    });
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      const prompt = prompts.find(p => p.id === item.id);
      return total + (prompt ? prompt.price * item.quantity : 0);
    }, 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      setMessage({ type: 'error', text: isArabic ? 'يرجى تسجيل الدخول أولاً' : 'Please login first' });
      return;
    }

    if (cart.length === 0) {
      setMessage({ type: 'error', text: isArabic ? 'السلة فارغة' : 'Cart is empty' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      // Build line items array for all cart items
      const lineItems = cart.map(cartItem => {
        const product = stripeProducts.find(p => p.id === cartItem.id);
        if (!product) {
          throw new Error(`Product not found for ID: ${cartItem.id}`);
        }
        return {
          price_id: product.priceId,
          quantity: cartItem.quantity
        };
      });

      // All products are payment mode for now
      const mode = 'payment';

      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        setMessage({ type: 'error', text: 'Authentication required' });
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line_items: lineItems,
          mode: mode,
          success_url: `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}/medical-prompts`,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to start checkout' });
    } finally {
      setLoading(false);
    }
  };
  const isArabic = currentLanguage === 'ar';

  return (
    <div className={`min-h-screen bg-black text-white py-12 px-4 ${isArabic ? 'rtl' : 'ltr'}`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className={`flex items-center justify-between mb-8 ${isArabic ? 'flex-row-reverse' : ''}`}>
          <Link 
            to="/" 
            className={`flex items-center text-gray-400 hover:text-white transition-colors duration-300`}
          >
            <ArrowLeft className={`w-5 h-5 ${isArabic ? 'ml-2 rotate-180' : 'mr-2'}`} />
            {isArabic ? 'العودة للرئيسية' : 'Back to Home'}
          </Link>
          
          <div className="flex items-center space-x-4">
            {/* Auth Status */}
            {isAuthenticated ? (
              <div className="text-sm text-gray-400">
                {isArabic ? 'مرحباً' : 'Welcome'}, {user?.email}
                {activeSubscriptionPlan && (
                  <div className="text-green-400">
                    {isArabic ? 'الخطة:' : 'Plan:'} {activeSubscriptionPlan}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex space-x-2">
                <Link
                  to="/login"
                  className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm transition-colors duration-300"
                >
                  {isArabic ? 'دخول' : 'Login'}
                </Link>
                <Link
                  to="/signup"
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm transition-colors duration-300"
                >
                  {isArabic ? 'تسجيل' : 'Sign Up'}
                </Link>
              </div>
            )}

            {/* Cart Button */}
            <button
              onClick={() => setShowCart(!showCart)}
              className="relative flex items-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-300"
            >
              <ShoppingCart className="w-5 h-5 mr-2" />
              {isArabic ? 'السلة' : 'Cart'}
              {getCartItemCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                  {getCartItemCount()}
                </span>
              )}
            </button>

            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-300"
            >
              <Globe className="w-5 h-5 mr-2" />
              {isArabic ? 'English' : 'العربية'}
            </button>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-900/30 border border-green-500/30 text-green-300' 
              : 'bg-red-900/30 border border-red-500/30 text-red-300'
          }`}>
            {message.text}
          </div>
        )}

        {/* Page Title */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Stethoscope className="w-12 h-12 text-blue-500 mr-3" />
            <h1 className="text-4xl font-bold">
              {isArabic ? 'القوالب الطبية' : 'Medical Prompts'}
            </h1>
          </div>
          <p className="text-gray-400 text-lg">
            {isArabic ? 'قوالب طبية احترافية لتحسين ممارستك الطبية' : 'Professional medical templates to enhance your medical practice'}
          </p>
        </div>

        {/* Shopping Cart Sidebar */}
        {showCart && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end">
            <div className="bg-gray-900 w-96 h-full p-6 overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">
                  {isArabic ? 'سلة التسوق' : 'Shopping Cart'}
                </h2>
                <button
                  onClick={() => setShowCart(false)}
                  className="text-gray-400 hover:text-white"
                >
                  ✕
                </button>
              </div>

              {cart.length === 0 ? (
                <p className="text-gray-400 text-center">
                  {isArabic ? 'السلة فارغة' : 'Cart is empty'}
                </p>
              ) : (
                <>
                  <div className="space-y-4 mb-6">
                    {cart.map(item => {
                      const prompt = prompts.find(p => p.id === item.id);
                      if (!prompt) return null;
                      
                      return (
                        <div key={item.id} className="bg-gray-800 rounded-lg p-4">
                          <h3 className="font-semibold mb-2">
                            {prompt[currentLanguage].title}
                          </h3>
                          <div className="flex items-center justify-between">
                            <span className="text-green-400 font-bold">
                              £{prompt.price}
                            </span>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => removeFromCart(item.id)}
                                className="bg-red-600 hover:bg-red-700 text-white w-8 h-8 rounded-full flex items-center justify-center"
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <span className="w-8 text-center">{item.quantity}</span>
                              <button
                                onClick={() => addToCart(item.id)}
                                className="bg-green-600 hover:bg-green-700 text-white w-8 h-8 rounded-full flex items-center justify-center"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <div className="text-sm text-gray-400 mt-2">
                            {isArabic ? 'المجموع:' : 'Subtotal:'} £{prompt.price * item.quantity}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="border-t border-gray-700 pt-4">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-xl font-bold">
                        {isArabic ? 'المجموع الكلي:' : 'Total:'}
                      </span>
                      <span className="text-xl font-bold text-green-400">
                        £{getCartTotal()}
                      </span>
                    </div>
                    
                    <button 
                      onClick={handleCheckout}
                      disabled={loading || cart.length === 0}
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-600 text-white py-3 rounded-lg font-medium transition-colors duration-300 flex items-center justify-center"
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      {isArabic ? 'الدفع بواسطة Stripe' : 'Checkout with Stripe'}
                        </>
                      )}
                    </button>
                    
                    <p className="text-xs text-gray-400 text-center mt-2">
                      {isArabic ? 'دفع آمن ومشفر' : 'Secure encrypted payment'}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Prompts Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {prompts.map((prompt) => (
            <div 
              key={prompt.id}
              className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 hover:from-gray-800 hover:to-gray-700 transition-all duration-300 transform hover:scale-105"
            >
              <div className={`${isArabic ? 'text-right' : 'text-left'}`}>
                {/* Icon and Category */}
                <div className={`flex items-center mb-4 ${isArabic ? 'flex-row-reverse' : ''}`}>
                  <div className="text-blue-500 mr-3">
                    {prompt.icon}
                  </div>
                  <span className="text-sm text-gray-400 bg-gray-700 px-2 py-1 rounded-full">
                    {prompt[currentLanguage].category}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-blue-400 mb-3">
                  {prompt[currentLanguage].title}
                </h3>
                
                {/* Description */}
                <p className="text-gray-400 mb-4">
                  {prompt[currentLanguage].description}
                </p>
                
                {/* Price and Add to Cart */}
                <div className={`flex items-center justify-between mb-4 ${isArabic ? 'flex-row-reverse' : ''}`}>
                  <div className="text-2xl font-bold text-green-400">
                    £{prompt.price}
                  </div>
                  <button
                    onClick={() => addToCart(prompt.id)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-300 flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {isArabic ? 'أضف للسلة' : 'Add to Cart'}
                  </button>
                </div>

                {/* Download PDF Button */}
                {prompt.pdfUrl && (
                  <a
                    href={prompt.pdfUrl}
                    download
                    className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-300 flex items-center justify-center"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {isArabic ? 'تحميل PDF' : 'Download PDF'}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-blue-900 to-purple-800 rounded-xl p-8">
          <h2 className="text-2xl font-bold mb-4">
            {isArabic ? 'تحتاج قالب مخصص؟' : 'Need a Custom Prompt?'}
          </h2>
          <p className="text-gray-300 mb-6">
            {isArabic ? 'تواصل معنا لإنشاء قوالب طبية مخصصة حسب تخصصك' : 'Contact us to create custom medical prompts for your specialty'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/96598551104"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-300"
            >
              {isArabic ? 'واتساب' : 'WhatsApp Us'}
            </a>
            <a
              href="https://instagram.com/cross_link.kw"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-300"
            >
              {isArabic ? 'رسالة إنستغرام' : 'Instagram DM'}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedicalPrompts;